import { Skeleton } from "@mui/material";
import clsx from "clsx";

/**
 * Which shape the placeholder takes while a section's content is loading.
 *
 * Pick the one matching what the section actually wraps — the point of a
 * skeleton is that the page doesn't reflow when the real content lands, so a
 * grid should not be stood in for by a paragraph.
 *
 * - `text`  — label/value detail panels (MeterInfo, SubscriberInfo, UserInfo…)
 * - `table` — grids and tabular lists
 * - `chart` — column/line Highcharts sections
 * - `pie`   — pie/donut Highcharts sections
 * - `map`   — Neshan/OpenLayers sections
 * - `block` — generic fallback: one shimmer sized by `minHeight`
 */
export type TSectionSkeleton =
  | "text"
  | "table"
  | "chart"
  | "pie"
  | "map"
  | "block";

/**
 * Default heights, chosen to match what the real content settles at, so the
 * skeleton→content swap doesn't jump the page: `chart` and `pie` mirror
 * `.chart-wrapper { min-height: 400px }` in Charts/style.css.
 */
const DEFAULT_MIN_HEIGHT: Record<TSectionSkeleton, number> = {
  text: 180,
  table: 320,
  chart: 400,
  pie: 400,
  map: 360,
  block: 160,
};

export const sectionSkeletonMinHeight = (
  variant: TSectionSkeleton,
  override?: number | string
) => override ?? DEFAULT_MIN_HEIGHT[variant];

type TProps = {
  variant: TSectionSkeleton;
  /** Overrides the variant's default height. */
  minHeight?: number | string;
  /** Rows for `text` / `table`, bars for `chart`, slices for `pie`. */
  rows?: number;
  /** Fewer rows/bars — used by the mobile section, which has less room. */
  dense?: boolean;
  className?: string;
};

/** Placeholder content for a loading CollapsibleSection. */
export function SectionSkeleton({
  variant,
  minHeight,
  rows,
  dense,
  className,
}: TProps) {
  const count = (full: number, tight: number) => rows ?? (dense ? tight : full);

  return (
    <div
      className={clsx("w-full", className)}
      style={{ minHeight: sectionSkeletonMinHeight(variant, minHeight) }}
      // Announced as busy rather than read out shimmer-by-shimmer.
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {variant === "text" && <TextSkeleton rows={count(6, 4)} />}
      {variant === "table" && <TableSkeleton rows={count(6, 4)} />}
      {variant === "chart" && <ChartSkeleton bars={count(9, 6)} />}
      {variant === "pie" && <PieSkeleton slices={count(4, 3)} dense={dense} />}
      {variant === "map" && <MapSkeleton />}
      {variant === "block" && (
        <Skeleton variant="rounded" height="100%" className="!min-h-[inherit]" />
      )}
    </div>
  );
}

/** Label/value pairs, two per row on wide screens — the detail-panel shape. */
function TextSkeleton({ rows }: { rows: number }) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <Skeleton variant="text" width="38%" height={12} />
          <Skeleton variant="text" width="72%" height={18} />
        </div>
      ))}
    </div>
  );
}

/** Toolbar, header band, then rows — the grid shape. */
function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-2">
        <Skeleton variant="rounded" width={28} height={28} />
        <Skeleton variant="rounded" width={28} height={28} />
        <Skeleton variant="rounded" width={28} height={28} />
      </div>
      <Skeleton variant="rounded" height={38} />
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} variant="rounded" height={30} />
      ))}
    </div>
  );
}

/**
 * A real plot frame: y-axis tick labels, gridlines, an axis baseline, columns
 * and x-axis tick labels. Bar heights come from the index, not Math.random, so
 * the placeholder is stable across re-renders — a reshuffling skeleton reads as
 * broken.
 */
function ChartSkeleton({ bars }: { bars: number }) {
  return (
    <div className="flex h-full flex-col gap-2" style={{ minHeight: "inherit" }}>
      <Skeleton variant="text" width="28%" height={14} />

      <div className="flex min-h-0 flex-1 gap-2">
        {/* y-axis ticks (leading edge — the right, under RTL) */}
        <div className="flex shrink-0 flex-col justify-between py-0.5">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} variant="text" width={26} height={9} />
          ))}
        </div>

        <div className="relative flex min-w-0 flex-1 flex-col">
          {/* gridlines behind the columns */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 bottom-7 flex flex-col justify-between"
          >
            {Array.from({ length: 5 }, (_, index) => (
              <span key={index} className="h-px w-full bg-neutral-200" />
            ))}
          </div>

          {/* columns */}
          <div className="relative flex flex-1 items-end gap-1.5 px-1 sm:gap-2.5">
            {Array.from({ length: bars }, (_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                className="flex-1 !transform-none"
                // Deterministic 30–95% sawtooth.
                height={`${30 + ((index * 37) % 66)}%`}
              />
            ))}
          </div>

          {/* axis baseline + x-axis ticks */}
          <span className="h-px w-full shrink-0 bg-neutral-300" />
          <div className="flex h-7 shrink-0 items-start gap-1.5 px-1 pt-2 sm:gap-2.5">
            {Array.from({ length: bars }, (_, index) => (
              <Skeleton
                key={index}
                variant="text"
                className="flex-1"
                height={8}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Disc plus a legend column — the pie/donut shape.
 *
 * The disc is sized by a percentage inside an `aspect-square` box rather than
 * fixed pixels, so it stays perfectly circular at any container width and only
 * grows or shrinks. Passing width/height props to MUI Skeleton would let the
 * two axes diverge as the column narrows.
 */
function PieSkeleton({ slices, dense }: { slices: number; dense?: boolean }) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-6 sm:flex-row"
      style={{ minHeight: "inherit" }}
    >
      <div
        className={clsx(
          "aspect-square w-[55%] shrink-0",
          dense ? "min-w-[96px] max-w-[150px]" : "min-w-[110px] max-w-[200px]"
        )}
      >
        <Skeleton
          variant="circular"
          width="100%"
          height="100%"
          className="!transform-none"
        />
      </div>
      <div className="flex flex-col gap-2.5">
        {Array.from({ length: slices }, (_, index) => (
          <div key={index} className="flex items-center gap-2">
            <Skeleton variant="circular" width={10} height={10} />
            <Skeleton variant="text" width={64 + ((index * 23) % 40)} height={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A full-bleed canvas with the control and legend stubs sitting on it. */
function MapSkeleton() {
  return (
    <div className="relative h-full w-full" style={{ minHeight: "inherit" }}>
      <Skeleton
        variant="rounded"
        height="100%"
        className="!min-h-[inherit] !transform-none"
      />
      <div className="absolute top-3 left-3 flex flex-col gap-1">
        <Skeleton variant="rounded" width={32} height={32} />
        <Skeleton variant="rounded" width={32} height={32} />
        <Skeleton variant="rounded" width={32} height={32} />
      </div>
      <div className="absolute bottom-3 right-3">
        <Skeleton variant="rounded" width={132} height={72} />
      </div>
    </div>
  );
}

export default SectionSkeleton;
